/**
 * Benefits Capture — the period fold the page renders from.
 *
 * The builder writes one counts-only doc per clinic per completed day
 * (`organizations/{orgId}/benefitsCaptureDays/{clinicId}_{day}`) plus a
 * `_status` doc naming the clinics and the go-live day. Everything on the
 * page is a sum of those docs over a window: this module computes the
 * windows (each clinic's own completed days, in its own timezone), the sums,
 * the whole-percent rates, the deltas against the prior window, the weekly
 * bars, the per-clinic rows and the reasons' shares. Pure, so the page can
 * be restyled without touching a query and the fold can be pinned by tests.
 */

import { DEFAULT_TIMEZONE } from '../../shared/timezone'

// ── The doc shape (the contract with the builder) ───────────────────────

export const REASON_KEYS = [
  'patient_info',
  'check_failed',
  'provider',
  'payer',
  'no_medical_plan',
  'no_insurance',
  'not_checked',
] as const
export type ReasonKey = (typeof REASON_KEYS)[number]
export type ReasonCounts = Record<ReasonKey, number>

export interface BenefitsCaptureDayDoc {
  clinicId: string
  clinicName: string
  clinicStatus: string
  day: string
  visits: number
  medicalVerified: number
  activeCoverage: number
  visionVerified: number
  visionOnly: number
  noInsurance: number
  reasons: ReasonCounts
  truncated?: boolean
}

export interface StatusClinic {
  clinicId: string
  name: string
  status: string
  timezone: string
}

export interface BenefitsCaptureStatusDoc {
  clinics: StatusClinic[]
  goLiveDay: string | null
}

export type PeriodKey = '7d' | '30d' | 'sinceGoLive'

export const PERIOD_LABELS: Record<PeriodKey, string> = {
  '7d': '7 days',
  '30d': '30 days',
  sinceGoLive: 'Since go-live',
}

/** The timezone a clinic without one is read in. */
export { DEFAULT_TIMEZONE }
/** The chart's window: the last 12 calendar weeks, the current one included. */
export const CHART_WEEKS = 12
/** "Lagging": this many points under the hero, on at least this many visits. */
export const LAGGING_POINTS = 10
export const LAGGING_MIN_VISITS = 20

// ── Calendar ────────────────────────────────────────────────────────────

/** The calendar day (YYYY-MM-DD) of an instant in a timezone — the same
 *  `Intl` call the backend uses, so a browser in another timezone changes
 *  nothing. */
export function localDayOf(timezone: string, at: Date): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || DEFAULT_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(at)
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: DEFAULT_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(at)
  }
}

/** Pure calendar arithmetic on a YYYY-MM-DD string (no timezone). */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n, 12)).toISOString().slice(0, 10)
}

/** Days from `from` to `to`, inclusive; negative when reversed. */
export function daySpan(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number)
  const [ty, tm, td] = to.split('-').map(Number)
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000) + 1
}

/** The Monday that starts the calendar week holding `day`. */
export function weekStartOf(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  const dow = new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay() // 0 = Sunday
  return addDays(day, dow === 0 ? -6 : 1 - dow)
}

export function yesterdayOf(clinic: StatusClinic, now: Date): string {
  return addDays(localDayOf(clinic.timezone, now), -1)
}

// ── Windows ─────────────────────────────────────────────────────────────

/** A run of completed days; `from: null` = no lower bound (every doc on record). */
export interface DayWindow {
  from: string | null
  to: string
}

/** The selected period for one clinic, ending on its own yesterday. */
export function periodWindow(period: PeriodKey, yesterday: string, goLiveDay: string | null): DayWindow {
  switch (period) {
    case '7d': return { from: addDays(yesterday, -6), to: yesterday }
    case '30d': return { from: addDays(yesterday, -29), to: yesterday }
    case 'sinceGoLive': return { from: goLiveDay, to: yesterday }
  }
}

/**
 * The window of equal length just before the period — null under "since
 * go-live", and null whenever any of its days precedes the go-live day, so a
 * delta shown always compares two windows of real days.
 */
export function priorWindow(period: PeriodKey, window: DayWindow, goLiveDay: string | null): DayWindow | null {
  if (period === 'sinceGoLive' || window.from === null) return null
  const len = daySpan(window.from, window.to)
  const from = addDays(window.from, -len)
  const to = addDays(window.from, -1)
  if (goLiveDay !== null && from < goLiveDay) return null
  return { from, to }
}

function inWindow(day: string, w: DayWindow): boolean {
  return (w.from === null || day >= w.from) && day <= w.to
}

// ── Sums and rates ──────────────────────────────────────────────────────

export interface Totals {
  visits: number
  medicalVerified: number
  activeCoverage: number
  visionVerified: number
  visionOnly: number
  noInsurance: number
  reasons: ReasonCounts
}

export function emptyReasons(): ReasonCounts {
  return {
    patient_info: 0, check_failed: 0, provider: 0, payer: 0,
    no_medical_plan: 0, no_insurance: 0, not_checked: 0,
  }
}

export function emptyTotals(): Totals {
  return {
    visits: 0, medicalVerified: 0, activeCoverage: 0, visionVerified: 0,
    visionOnly: 0, noInsurance: 0, reasons: emptyReasons(),
  }
}

export function addDoc(t: Totals, d: BenefitsCaptureDayDoc): void {
  t.visits += d.visits
  t.medicalVerified += d.medicalVerified
  t.activeCoverage += d.activeCoverage
  t.visionVerified += d.visionVerified
  t.visionOnly += d.visionOnly
  t.noInsurance += d.noInsurance
  for (const k of REASON_KEYS) t.reasons[k] += d.reasons?.[k] ?? 0
}

export function sumDocs(docs: Iterable<BenefitsCaptureDayDoc>): Totals {
  const t = emptyTotals()
  for (const d of docs) addDoc(t, d)
  return t
}

/** Whole percent, or null when there is nothing to divide by. */
export function pct(part: number, whole: number): number | null {
  return whole > 0 ? Math.round((part / whole) * 100) : null
}

/** Points between two whole-percent values; null when either is undefined. */
export function pointsDelta(current: number | null, prior: number | null): number | null {
  return current === null || prior === null ? null : current - prior
}

export function countDelta(current: number, prior: number | null): number | null {
  return prior === null ? null : current - prior
}

/**
 * Whole-percent shares that total exactly 100 (largest remainder), or all
 * zero when there is nothing to share.
 */
export function largestRemainderShares(counts: number[]): number[] {
  const total = counts.reduce((a, b) => a + b, 0)
  if (total <= 0) return counts.map(() => 0)
  const exact = counts.map((c) => (c / total) * 100)
  const floors = exact.map(Math.floor)
  let remaining = 100 - floors.reduce((a, b) => a + b, 0)
  const order = exact
    .map((v, i) => ({ i, frac: v - floors[i] }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i)
  for (const { i } of order) {
    if (remaining <= 0) break
    floors[i] += 1
    remaining -= 1
  }
  return floors
}

// ── The page's numbers ──────────────────────────────────────────────────

export interface ClinicRow {
  clinicId: string
  name: string
  archived: boolean
  visits: number
  /** The clinic's X% over the period; null with no visits. */
  share: number | null
  lagging: boolean
}

export interface WeekBar {
  /** Monday. */
  start: string
  /** Sunday. */
  end: string
  visits: number
  verified: number
  share: number | null
  /** The current week: completed days only. */
  partial: boolean
}

export interface ReasonRow {
  key: ReasonKey
  count: number
  share: number
}

export interface PeriodResult {
  period: PeriodKey
  goLiveDay: string | null
  /** Earliest window start across clinics (null = every doc on record) to
   *  the latest clinic yesterday. */
  window: DayWindow
  current: Totals
  prior: Totals | null
  hero: {
    share: number | null
    deltaPts: number | null
    visionOnly: number
    visionOnlyDelta: number | null
  }
  activeRate: { share: number | null; deltaPts: number | null }
  recoverable: { estimate: number | null; delta: number | null }
  tiles: {
    visits: { count: number; delta: number | null }
    visionVerified: { count: number; share: number | null; deltaPts: number | null }
    medicalVerified: { count: number; share: number | null; deltaPts: number | null; noAnswer: number }
    activeCoverage: { count: number; bar: number | null; rate: number | null; deltaPts: number | null }
  }
  reasons: ReasonRow[]
  clinics: ClinicRow[]
  weeks: WeekBar[]
  /** The newest day every active clinic has a doc for; null while one has none. */
  dataThrough: string | null
  /** Expected clinic-days inside the requested windows with no doc yet. */
  missingDays: number
  partial: boolean
  /** Any doc in the period hit the builder's row cap. */
  truncated: boolean
}

export interface FoldInput {
  period: PeriodKey
  status: BenefitsCaptureStatusDoc
  docs: BenefitsCaptureDayDoc[]
  now: Date
}

function clinicTimezone(c: StatusClinic): string {
  return c.timezone || DEFAULT_TIMEZONE
}

/** Where the page's listener starts: the earliest of the period start, the
 *  prior window start and the chart's first Monday, across clinics; null =
 *  no lower bound. */
export function fetchFrom(period: PeriodKey, status: BenefitsCaptureStatusDoc, now: Date): string | null {
  let from: string | null = null
  let bounded = true
  const consider = (day: string | null) => {
    if (day === null) { bounded = false; return }
    if (from === null || day < from) from = day
  }
  for (const c of status.clinics) {
    const yesterday = yesterdayOf({ ...c, timezone: clinicTimezone(c) }, now)
    const w = periodWindow(period, yesterday, status.goLiveDay)
    consider(w.from)
    const p = priorWindow(period, w, status.goLiveDay)
    if (p) consider(p.from)
    const today = localDayOf(clinicTimezone(c), now)
    consider(addDays(weekStartOf(today), -7 * (CHART_WEEKS - 1)))
  }
  if (period === 'sinceGoLive' && status.goLiveDay === null) return null
  return bounded ? from : null
}

/** The latest of the clinics' yesterdays — the day the page can run through
 *  at best; null with no clinics. */
export function latestYesterday(status: BenefitsCaptureStatusDoc, now: Date): string | null {
  let latest: string | null = null
  for (const c of status.clinics) {
    const y = yesterdayOf({ ...c, timezone: clinicTimezone(c) }, now)
    if (latest === null || y > latest) latest = y
  }
  return latest
}

export function foldPeriod(input: FoldInput): PeriodResult {
  const { period, status, docs, now } = input
  const goLiveDay = status.goLiveDay
  const clinics = status.clinics.map((c) => ({ ...c, timezone: clinicTimezone(c) }))
  const byClinic = new Map<string, BenefitsCaptureDayDoc[]>()
  for (const d of docs) {
    const list = byClinic.get(d.clinicId) ?? []
    list.push(d)
    byClinic.set(d.clinicId, list)
  }
  // Docs of a clinic the status doc no longer names (deleted) still count
  // toward the org: they are real visits. They ride under their own id.
  const known = new Set(clinics.map((c) => c.clinicId))
  for (const clinicId of byClinic.keys()) {
    if (!known.has(clinicId)) {
      const sample = byClinic.get(clinicId)?.[0]
      clinics.push({
        clinicId, name: sample?.clinicName ?? clinicId,
        status: sample?.clinicStatus ?? 'archived', timezone: DEFAULT_TIMEZONE,
      })
    }
  }

  const current = emptyTotals()
  const prior = emptyTotals()
  let anyPrior = period !== 'sinceGoLive'
  let windowFrom: string | null = null
  let windowFromUnbounded = false
  let windowTo: string | null = null
  const clinicRows: ClinicRow[] = []
  let missingDays = 0
  let truncated = false
  let dataThrough: string | null | undefined = undefined
  const yesterdays = new Map<string, string>()

  for (const clinic of clinics) {
    const yesterday = yesterdayOf(clinic, now)
    yesterdays.set(clinic.clinicId, yesterday)
    const w = periodWindow(period, yesterday, goLiveDay)
    const p = priorWindow(period, w, goLiveDay)
    if (p === null) anyPrior = false
    if (w.from === null) windowFromUnbounded = true
    else if (windowFrom === null || w.from < windowFrom) windowFrom = w.from
    if (windowTo === null || w.to > windowTo) windowTo = w.to

    const own = byClinic.get(clinic.clinicId) ?? []
    const inPeriod = own.filter((d) => inWindow(d.day, w))
    const t = sumDocs(inPeriod)
    for (const d of inPeriod) {
      addDoc(current, d)
      if (d.truncated) truncated = true
    }
    if (p) for (const d of own) if (inWindow(d.day, p)) addDoc(prior, d)

    const archived = clinic.status === 'archived'
    if (!archived || t.visits > 0) {
      clinicRows.push({
        clinicId: clinic.clinicId, name: clinic.name, archived,
        visits: t.visits, share: pct(t.medicalVerified, t.visits), lagging: false,
      })
    }

    if (!archived) {
      // Expected: every day from go-live to this clinic's yesterday, inside
      // the windows the page asked for (period, prior, chart). Without a
      // go-live day nothing is expected, so nothing can be missing.
      const built = new Set(own.map((d) => d.day))
      const newest = own.reduce<string | null>((m, d) => (m === null || d.day > m ? d.day : m), null)
      if (newest === null) dataThrough = null
      else if (dataThrough === undefined || (dataThrough !== null && newest < dataThrough)) dataThrough = newest
      if (goLiveDay !== null) {
        const chartFrom = addDays(weekStartOf(localDayOf(clinic.timezone, now)), -7 * (CHART_WEEKS - 1))
        const lows = [w.from, p?.from ?? null, chartFrom].filter((x): x is string => x !== null)
        const lowest = lows.reduce((a, b) => (a < b ? a : b))
        const from = lowest > goLiveDay ? lowest : goLiveDay
        for (let day = from; day <= yesterday; day = addDays(day, 1)) {
          if (!built.has(day)) missingDays++
        }
      }
    }
  }

  const heroShare = pct(current.medicalVerified, current.visits)
  const priorTotals = anyPrior ? prior : null
  const priorHeroShare = priorTotals ? pct(priorTotals.medicalVerified, priorTotals.visits) : null
  const activeRate = pct(current.activeCoverage, current.medicalVerified)
  const priorActiveRate = priorTotals ? pct(priorTotals.activeCoverage, priorTotals.medicalVerified) : null
  const estimate = activeRate === null ? null : Math.round((current.visionOnly * activeRate) / 100)
  const priorEstimate = priorActiveRate === null || priorTotals === null
    ? null
    : Math.round((priorTotals.visionOnly * priorActiveRate) / 100)

  for (const row of clinicRows) {
    row.lagging = row.share !== null && heroShare !== null &&
      row.visits >= LAGGING_MIN_VISITS && heroShare - row.share >= LAGGING_POINTS
  }
  clinicRows.sort((a, b) => {
    if (a.share === null && b.share === null) return a.name.localeCompare(b.name)
    if (a.share === null) return 1
    if (b.share === null) return -1
    return b.share - a.share || a.name.localeCompare(b.name)
  })

  const reasonCounts = REASON_KEYS.map((k) => current.reasons[k])
  const shares = largestRemainderShares(reasonCounts)
  const reasons: ReasonRow[] = REASON_KEYS.map((key, i) => ({ key, count: reasonCounts[i], share: shares[i] }))
  reasons.sort((a, b) => b.count - a.count || REASON_KEYS.indexOf(a.key) - REASON_KEYS.indexOf(b.key))

  return {
    period,
    goLiveDay,
    window: { from: windowFromUnbounded ? null : windowFrom, to: windowTo ?? localDayOf(DEFAULT_TIMEZONE, now) },
    current,
    prior: priorTotals,
    hero: {
      share: heroShare,
      deltaPts: pointsDelta(heroShare, priorHeroShare),
      visionOnly: current.visionOnly,
      visionOnlyDelta: countDelta(current.visionOnly, priorTotals ? priorTotals.visionOnly : null),
    },
    activeRate: { share: activeRate, deltaPts: pointsDelta(activeRate, priorActiveRate) },
    recoverable: {
      estimate,
      delta: estimate === null || priorEstimate === null ? null : estimate - priorEstimate,
    },
    tiles: {
      visits: { count: current.visits, delta: countDelta(current.visits, priorTotals ? priorTotals.visits : null) },
      visionVerified: {
        count: current.visionVerified,
        share: pct(current.visionVerified, current.visits),
        deltaPts: pointsDelta(
          pct(current.visionVerified, current.visits),
          priorTotals ? pct(priorTotals.visionVerified, priorTotals.visits) : null,
        ),
      },
      medicalVerified: {
        count: current.medicalVerified,
        share: heroShare,
        deltaPts: pointsDelta(heroShare, priorHeroShare),
        noAnswer: current.visits - current.medicalVerified,
      },
      activeCoverage: {
        count: current.activeCoverage,
        bar: pct(current.activeCoverage, current.visits),
        rate: activeRate,
        deltaPts: pointsDelta(activeRate, priorActiveRate),
      },
    },
    reasons,
    clinics: clinicRows,
    weeks: weeklyBars(docs, status, now),
    dataThrough: dataThrough ?? null,
    missingDays,
    partial: missingDays > 0 || truncated,
    truncated,
  }
}

/**
 * The chart: one bar per calendar week (Monday–Sunday) over the last
 * CHART_WEEKS weeks ending with the current week, every clinic pooled, weeks
 * before the go-live week dropped. The current week holds its completed days
 * only (no doc exists for a day still running) and is labeled partial.
 */
export function weeklyBars(docs: BenefitsCaptureDayDoc[], status: BenefitsCaptureStatusDoc, now: Date): WeekBar[] {
  const todayByClinic = new Map(status.clinics.map((c) => [c.clinicId, localDayOf(clinicTimezone(c), now)]))
  const todays = [...todayByClinic.values()]
  if (todays.length === 0) todays.push(localDayOf(DEFAULT_TIMEZONE, now))
  const latestToday = todays.reduce((a, b) => (a > b ? a : b))
  const earliestToday = todays.reduce((a, b) => (a < b ? a : b))
  // Completed days only: a doc dated a clinic's today or later never counts.
  const completed = docs.filter((d) => d.day < (todayByClinic.get(d.clinicId) ?? latestToday))
  const currentStart = weekStartOf(latestToday)
  const goLiveWeek = status.goLiveDay === null ? null : weekStartOf(status.goLiveDay)
  const bars: WeekBar[] = []
  for (let i = CHART_WEEKS - 1; i >= 0; i--) {
    const start = addDays(currentStart, -7 * i)
    if (goLiveWeek !== null && start < goLiveWeek) continue
    const end = addDays(start, 6)
    let visits = 0
    let verified = 0
    for (const d of completed) {
      if (d.day >= start && d.day <= end) {
        visits += d.visits
        verified += d.medicalVerified
      }
    }
    bars.push({
      start, end, visits, verified,
      share: pct(verified, visits),
      partial: end >= earliestToday,
    })
  }
  return bars
}
